import Collapse from 'bootstrap/js/dist/collapse.js'

const CONTAINER_SELECTOR = '[data-shopping-cart-user-input]'
const INPUT_SELECTOR = '[data-shopping-cart-user-input-control]'
const VALUE_SELECTOR = '[data-shopping-cart-user-input-value]'
const COLLAPSE_SELECTOR = '[data-shopping-cart-user-input-collapse]'
const FORM_SELECTOR = '[data-product-configurator]'

export default class ShoppingCartUserInput {
  constructor() {
    this.handleChange = this.handleChange.bind(this)
    this.handleContentLoaded = this.handleContentLoaded.bind(this)
  }

  init() {
    this.initialize(document)

    document.addEventListener('change', this.handleChange)
    document.addEventListener('remote-modal:content-loaded', this.handleContentLoaded)
  }

  handleContentLoaded(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    this.initialize(event.target)
  }

  initialize(root) {
    const containers = []

    if (root.matches?.(CONTAINER_SELECTOR)) {
      containers.push(root)
    }

    root.querySelectorAll?.(CONTAINER_SELECTOR).forEach((container) => {
      containers.push(container)
    })

    containers.forEach((container) => {
      this.initializeContainer(container)
    })
  }

  initializeContainer(container) {
    if (!(container instanceof HTMLElement)) {
      return
    }

    const input = container.querySelector(INPUT_SELECTOR)
    const valueInput = container.querySelector(VALUE_SELECTOR)
    const form = container.closest(FORM_SELECTOR)
    const optionValueId = container.dataset.optionValueId

    if (!(input instanceof HTMLInputElement)) {
      return
    }

    if (!(valueInput instanceof HTMLInputElement)) {
      return
    }

    if (!(form instanceof HTMLFormElement) || !optionValueId) {
      return
    }

    /*
     * Bij een opnieuw gerenderd formulier kan Symfony al een waarde
     * in shoppingcartItemUserInput.userInput hebben gezet.
     */
    if (valueInput.value && !input.value) {
      input.value = valueInput.value
    }

    const select = this.findSelect(form, optionValueId)

    if (!(select instanceof HTMLSelectElement)) {
      return
    }

    const isUserInputOptionSelected = select.value === optionValueId

    input.required = isUserInputOptionSelected

    /*
     * Als het formulier opnieuw gerenderd wordt terwijl de speciale
     * optie geselecteerd is, zorgen we dat het invoerveld zichtbaar is.
     */
    if (isUserInputOptionSelected) {
      this.showCollapse(container)
    }
  }

  handleChange(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    if (event.target.matches(INPUT_SELECTOR)) {
      this.handleUserInputChange(event.target)

      return
    }

    if (event.target instanceof HTMLSelectElement) {
      this.handleOptionChange(event.target)
    }
  }

  handleUserInputChange(input) {
    const container = input.closest(CONTAINER_SELECTOR)

    if (!(container instanceof HTMLElement)) {
      return
    }

    const form = container.closest(FORM_SELECTOR)
    const valueInput = container.querySelector(VALUE_SELECTOR)
    const optionValueId = container.dataset.optionValueId
    const userInput = input.value.trim()

    if (!(form instanceof HTMLFormElement)) {
      return
    }

    if (!(valueInput instanceof HTMLInputElement)) {
      return
    }

    valueInput.value = userInput

    if (!optionValueId) {
      return
    }

    const select = this.findSelect(form, optionValueId)

    if (!(select instanceof HTMLSelectElement)) {
      return
    }

    /*
     * Als de gebruiker de invoer leegmaakt terwijl de speciale optie
     * geselecteerd is, zetten we de select terug naar leeg.
     */
    if (!userInput) {
      if (select.value === optionValueId) {
        this.setSelectValue(select, '')
      }

      return
    }

    /*
     * Zodra er een vrije invoer wordt opgegeven, selecteren we
     * automatisch de gekoppelde productoptie.
     */
    this.setSelectValue(select, optionValueId)
  }

  handleOptionChange(select) {
    const form = select.closest(FORM_SELECTOR)

    if (!(form instanceof HTMLFormElement)) {
      return
    }

    form.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      if (!(container instanceof HTMLElement)) {
        return
      }

      const optionValueId = container.dataset.optionValueId

      if (!optionValueId) {
        return
      }

      /*
       * Alleen reageren als dit de select is waarin de gekoppelde
       * speciale productoptie voorkomt.
       */
      if (!this.selectContainsValue(select, optionValueId)) {
        return
      }

      const input = container.querySelector(INPUT_SELECTOR)

      if (!(input instanceof HTMLInputElement)) {
        return
      }

      const isUserInputOptionSelected = select.value === optionValueId

      input.required = isUserInputOptionSelected

      if (isUserInputOptionSelected) {
        this.showCollapse(container)

        return
      }

      this.clearUserInput(container)
    })
  }

  findSelect(form, optionValueId) {
    return Array.from(form.querySelectorAll('select')).find((select) => {
      return this.selectContainsValue(select, optionValueId)
    })
  }

  selectContainsValue(select, optionValueId) {
    if (select.dataset.specialColorOptionId === optionValueId) {
      return true
    }

    return Array.from(select.options).some((option) => {
      return option.value === optionValueId
    })
  }

  setSelectValue(select, value) {
    if (select.value === value) {
      return
    }

    /*
     * SearchableSelect gebruikt Tom Select. Via de Tom Select API blijft
     * zowel het echte select-element als de zichtbare combobox synchroon.
     */
    if (select.tomselect) {
      if (value && !select.tomselect.options[value] && select.dataset.specialColorOptionId === value) {
        select.tomselect.addOption({
          value,
          text: select.dataset.specialColorOptionLabel || value,
        })
      }

      select.tomselect.setValue(value)

      return
    }

    select.value = value

    select.dispatchEvent(
      new Event('change', {
        bubbles: true,
      }),
    )
  }

  showCollapse(container) {
    const collapseElement = container.querySelector(COLLAPSE_SELECTOR)

    if (!(collapseElement instanceof HTMLElement)) {
      return
    }

    Collapse.getOrCreateInstance(collapseElement, {
      toggle: false,
    }).show()
  }

  clearUserInput(container) {
    const input = container.querySelector(INPUT_SELECTOR)
    const valueInput = container.querySelector(VALUE_SELECTOR)

    if (input instanceof HTMLInputElement) {
      input.value = ''
      input.required = false
    }

    if (valueInput instanceof HTMLInputElement) {
      valueInput.value = ''
    }
  }
}
